import { useEffect, useState } from '../lib/teact/teact';

import type { BundleModules, Bundles } from '../util/moduleLoader';

import { DEBUG } from '../config';
import { addLoadListener, getModuleFromMemory, loadModule } from '../util/moduleLoader';
import { recoverFromStaleBuild } from '../util/recoverStaleBuild';
import useForceUpdate from './useForceUpdate';

// A failed chunk request used to be final. `loadModule` never resolved, so `forceUpdate` never ran,
// and none of the deps below changed — leaving the `.async` component rendering `undefined` for the
// rest of the session. For `Main` and `LockScreen` that component IS the app root, so the page showed
// nothing but the themed body background: white on the light theme, black on the dark one.
// Retry on a backoff for a network blip, then fall back to the same stale-build recovery the API
// worker connector uses — a chunk that 404s is the signature of a deploy having replaced this build's
// content-hashed files while the tab stayed open, which is why it only ever bites a long-lived tab.
const RETRY_DELAYS_MS = [1000, 3000, 10000];

const useModuleLoader = <B extends Bundles, M extends BundleModules<B>>(
  bundleName: B, moduleName: M, noLoad = false, autoUpdate = false,
) => {
  const module = getModuleFromMemory(bundleName, moduleName);
  const forceUpdate = useForceUpdate();
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!autoUpdate) {
      return undefined;
    }

    return addLoadListener(forceUpdate);
  }, [autoUpdate, forceUpdate]);

  useEffect(() => {
    if (noLoad || module) {
      return undefined;
    }

    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('Module load triggered', bundleName, moduleName);
    }

    let retryTimeout: number | undefined;

    loadModule(bundleName).then(forceUpdate, (err) => {
      // eslint-disable-next-line no-console
      console.error('Module load failed', bundleName, moduleName, err);

      const delay = RETRY_DELAYS_MS[attempt];
      // Out of retries: the chunk is not coming back on this build, so reload onto the deployed one
      // rather than leave the user looking at an empty page
      if (delay === undefined) {
        void recoverFromStaleBuild();
        return;
      }

      retryTimeout = window.setTimeout(() => setAttempt(attempt + 1), delay);
    });

    return () => {
      if (retryTimeout) window.clearTimeout(retryTimeout);
    };
  }, [attempt, bundleName, forceUpdate, module, moduleName, noLoad]);

  return module;
};

export default useModuleLoader;
